// frontend/src/superadmin/SuperAdminDashboard.js
// ═══════════════════════════════════════════════════════════════
// Global Panel SuperAdmin - UrbanQ v1.8.9.4.5.7
// Sistema de Administración de Condominios
// VERSIÓN CON FUNCIONALIDAD COMPLETA
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
  FaHome,
  FaBuilding,
  FaUsers,
  FaChartBar,
  FaCog,
  FaSignOutAlt,
  FaUser,
  FaDollarSign,
  FaExclamationTriangle,
  FaCheckCircle,
  FaBell,
  FaGlobe,
  FaServer,
  FaDatabase,
  FaShieldAlt,
  FaChevronLeft,
  FaChevronRight,
  FaBars,
  FaTimes,
  FaMicrochip,
  FaMemory,
  FaHdd,
  FaFileAlt,
  FaClipboardList,
  FaSave,
  FaSync,
  FaPlus,
  FaEdit,
  FaEye,
  FaTrash,
  FaDownload,
  FaEnvelope,
  FaLock,
  FaKey,
  FaClock,
  FaFileExcel,
  FaFilePdf,
  FaSpinner,
  FaCheck,
  FaPaypal,
  FaCommentDots
} from 'react-icons/fa';
import './SuperAdminDashboard.css';
import { API_URL, apiRequest, API_ENDPOINTS } from '../config/api';
import SuperAdminAIChat from './SuperAdminAIChat';

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: ChatBubble - Widget flotante para asistente AI
// ═══════════════════════════════════════════════════════════════
const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, type: 'bot', text: '¡Hola! Soy el asistente UrbanQ. ¿En qué puedo ayudarte hoy?' }
  ]);
  const [inputValue, setInputValue] = useState('');

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const newMessage = {
      id: messages.length + 1,
      type: 'user',
      text: inputValue
    };

    setMessages([...messages, newMessage]);
    setInputValue('');

    // Respuesta placeholder del bot (futuro: conectar con AI)
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: prev.length + 1,
        type: 'bot',
        text: 'Esta función estará disponible próximamente. El asistente AI está en desarrollo.'
      }]);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="sa-chat-container">
      {/* Ventana del chat */}
      {isOpen && (
        <div className="sa-chat-window">
          <div className="sa-chat-header">
            <div className="sa-chat-header-info">
              <FaCommentDots className="sa-chat-header-icon" />
              <span>Asistente UrbanQ</span>
            </div>
            <button className="sa-chat-close" onClick={toggleChat}>
              <FaTimes />
            </button>
          </div>
          <div className="sa-chat-messages">
            {messages.map(msg => (
              <div key={msg.id} className={`sa-chat-message ${msg.type}`}>
                {msg.text}
              </div>
            ))}
          </div>
          <div className="sa-chat-input-area">
            <input
              type="text"
              placeholder="Escribe tu mensaje..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              className="sa-chat-input"
            />
            <button className="sa-chat-send" onClick={handleSendMessage}>
              <FaChevronRight />
            </button>
          </div>
        </div>
      )}

      {/* Burbuja flotante */}
      <button className="sa-chat-bubble" onClick={toggleChat}>
        {isOpen ? <FaTimes /> : <FaCommentDots />}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// HELPER: Formatear bytes
// ═══════════════════════════════════════════════════════════════
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 GB';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Toast/Notificación
// ═══════════════════════════════════════════════════════════════
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`sa-toast ${type}`}>
      {type === 'success' && <FaCheckCircle />}
      {type === 'error' && <FaExclamationTriangle />}
      {type === 'loading' && <FaSpinner className="spinning" />}
      <span>{message}</span>
      <button onClick={onClose}><FaTimes /></button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Modal Genérico
// ═══════════════════════════════════════════════════════════════
const Modal = ({ isOpen, onClose, title, children, size = 'medium' }) => {
  if (!isOpen) return null;

  return (
    <div className="sa-modal-overlay" onClick={onClose}>
      <div className={`sa-modal ${size}`} onClick={e => e.stopPropagation()}>
        <div className="sa-modal-header">
          <h3>{title}</h3>
          <button className="sa-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="sa-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Indicador En Línea con Animación Glow
// ═══════════════════════════════════════════════════════════════
const OnlineIndicator = ({ isOnline }) => (
  <div className={`sa-online-indicator ${isOnline ? 'online' : 'offline'}`}>
    <div className="sa-online-dot"></div>
    <span>{isOnline ? 'En Línea' : 'Desconectado'}</span>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Card de Estadística del Sistema
// ═══════════════════════════════════════════════════════════════
const SystemStatCard = ({ icon: Icon, title, value, subtitle, color, progress }) => (
  <div className="sa-stat-card">
    <div className="sa-stat-card-header">
      <div className="sa-stat-icon" style={{ color }}>
        <Icon />
      </div>
      <span className="sa-stat-title">{title}</span>
    </div>
    <div className="sa-stat-card-body">
      {progress !== undefined ? (
        <>
          <div className="sa-stat-progress-bar">
            <div
              className="sa-stat-progress-fill"
              style={{
                width: `${Math.min(progress, 100)}%`,
                background: progress > 80 ? '#ef4444' : progress > 60 ? '#f59e0b' : '#10b981'
              }}
            ></div>
          </div>
          <div className="sa-stat-value">{value}</div>
        </>
      ) : (
        <div className="sa-stat-value large">{value}</div>
      )}
      {subtitle && <div className="sa-stat-subtitle">{subtitle}</div>}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: SuperAdmin Inicio
// ═══════════════════════════════════════════════════════════════
const SuperAdminInicio = ({ user, dashboardData, systemStats, setActiveSection }) => {
  const totalCondominiums = dashboardData?.totalCondominiums || 0;
  const totalUsers = dashboardData?.users?.User || 0;
  const totalAdmins = dashboardData?.users?.Admin || 0;
  const monthlyRevenue = dashboardData?.revenue?.monthly || 0;
  const systemStatus = systemStats?.status || 'online';

  return (
    <div className="sa-inicio">
      <div className="sa-welcome-card">
        <div className="sa-welcome-content">
          <h2>¡Bienvenido, {user.name}!</h2>
          <p>Global Panel de Administración - UrbanQ</p>
        </div>
        <div className="sa-welcome-icon">
          <FaShieldAlt />
        </div>
      </div>

      <div className="sa-quick-stats">
        <div className="sa-stat-card clickable" onClick={() => setActiveSection('condominiums')}>
          <div className="sa-stat-icon"><FaBuilding /></div>
          <h3>{totalCondominiums}</h3>
          <p>Condominios Activos</p>
        </div>
        <div className="sa-stat-card clickable" onClick={() => setActiveSection('admins')}>
          <div className="sa-stat-icon"><FaUsers /></div>
          <h3>{totalUsers + totalAdmins}</h3>
          <p>Usuarios Totales</p>
        </div>
        <div className="sa-stat-card clickable">
          <div className="sa-stat-icon"><FaDollarSign /></div>
          <h3>${monthlyRevenue.toLocaleString()}</h3>
          <p>Ingresos del Mes</p>
        </div>
        <div className="sa-stat-card clickable" onClick={() => setActiveSection('stats')}>
          <div className="sa-stat-icon"><FaCheckCircle /></div>
          <h3>{systemStatus === 'online' ? 'OK' : 'Error'}</h3>
          <p>Estado del Sistema</p>
        </div>
      </div>

      <div className="sa-quick-actions">
        <button onClick={() => setActiveSection('condominiums')}>
          <FaBuilding /> Gestionar Condominios
        </button>
        <button onClick={() => setActiveSection('stats')}>
          <FaChartBar /> Ver Estadísticas
        </button>
        <button onClick={() => setActiveSection('reports')}>
          <FaFileAlt /> Reportería
        </button>
        <button onClick={() => setActiveSection('settings')}>
          <FaCog /> Configuración
        </button>
      </div>

      {dashboardData?.expiringSubscriptions?.length > 0 && (
        <div className="sa-expiring-section">
          <h3><FaExclamationTriangle /> Suscripciones por Vencer (7 días)</h3>
          <div className="sa-expiring-list">
            {dashboardData.expiringSubscriptions.map((sub, idx) => (
              <div key={idx} className="sa-expiring-item">
                <span className="sa-expiring-name">{sub.condominium_name}</span>
                <span className="sa-expiring-admin">{sub.name}</span>
                <span className="sa-expiring-days">{sub.days_remaining} días</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Gestión de Condominios (CON FUNCIONALIDAD COMPLETA)
// ═══════════════════════════════════════════════════════════════
const CondominiumsManager = ({ projects, loading, onRefresh, showToast }) => {
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCondo, setSelectedCondo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCondo, setNewCondo] = useState({
    name: '',
    address: '',
    admin_email: '',
    admin_name: '',
    subscription_plan: 'monthly'
  });

  const handleCreateCondo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await apiRequest(API_ENDPOINTS.superAdminProjects, {
        method: 'POST',
        body: JSON.stringify(newCondo)
      });
      if (response.success) {
        showToast('Condominio creado exitosamente', 'success');
        setShowNewModal(false);
        setNewCondo({ name: '', address: '', admin_email: '', admin_name: '', subscription_plan: 'monthly' });
        onRefresh();
      }
    } catch (error) {
      showToast(error.message || 'Error al crear condominio', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCondo = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await apiRequest(`${API_ENDPOINTS.superAdminProjects}/${selectedCondo.id}`, {
        method: 'PUT',
        body: JSON.stringify(selectedCondo)
      });
      if (response.success) {
        showToast('Condominio actualizado exitosamente', 'success');
        setShowEditModal(false);
        onRefresh();
      }
    } catch (error) {
      showToast(error.message || 'Error al actualizar condominio', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCondo = async (condo) => {
    if (!window.confirm(`¿Estás seguro de eliminar el condominio "${condo.name}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      const response = await apiRequest(`${API_ENDPOINTS.superAdminProjects}/${condo.id}`, {
        method: 'DELETE'
      });
      if (response.success) {
        showToast('Condominio eliminado', 'success');
        onRefresh();
      }
    } catch (error) {
      showToast(error.message || 'Error al eliminar condominio', 'error');
    }
  };

  const openEditModal = (condo) => {
    setSelectedCondo({ ...condo });
    setShowEditModal(true);
  };

  const openDetailsModal = (condo) => {
    setSelectedCondo(condo);
    setShowDetailsModal(true);
  };

  return (
    <div className="sa-section">
      <div className="sa-section-header">
        <div>
          <h2><FaBuilding /> Gestión de Condominios</h2>
          <p>Administra todos los condominios registrados en el sistema</p>
        </div>
        <button className="sa-btn-refresh" onClick={onRefresh} disabled={loading}>
          <FaSync className={loading ? 'spinning' : ''} />
        </button>
      </div>

      <div className="sa-section-actions">
        <button className="sa-btn-primary" onClick={() => setShowNewModal(true)}>
          <FaPlus /> Nuevo Condominio
        </button>
      </div>

      {loading ? (
        <div className="sa-loading-inline">Cargando condominios...</div>
      ) : (
        <div className="sa-condominiums-grid">
          {projects?.map(condo => (
            <div key={condo.id} className="sa-condo-card">
              <div className="sa-condo-header">
                <h3>{condo.name}</h3>
                <span className={`sa-status-badge ${condo.subscription_status?.toLowerCase() || 'active'}`}>
                  {condo.subscription_status || 'Active'}
                </span>
              </div>
              <div className="sa-condo-body">
                <p><FaGlobe /> {condo.address || 'Sin dirección'}</p>
                <p><FaUsers /> {condo.total_residents || 0} residentes</p>
                <p><FaDollarSign /> ${parseFloat(condo.total_revenue || 0).toLocaleString()} ingresos</p>
                {condo.days_remaining !== null && (
                  <p className={condo.days_remaining <= 7 ? 'warning' : ''}>
                    <FaClock /> {condo.days_remaining} días restantes
                  </p>
                )}
              </div>
              <div className="sa-condo-actions">
                <button className="sa-btn-outline" onClick={() => openEditModal(condo)}>
                  <FaEdit /> Editar
                </button>
                <button className="sa-btn-outline" onClick={() => openDetailsModal(condo)}>
                  <FaEye /> Ver Detalles
                </button>
                <button className="sa-btn-outline danger" onClick={() => handleDeleteCondo(condo)}>
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
          {(!projects || projects.length === 0) && (
            <div className="sa-empty-state">
              <FaBuilding />
              <p>No hay condominios registrados</p>
              <button className="sa-btn-primary" onClick={() => setShowNewModal(true)}>
                <FaPlus /> Crear Primer Condominio
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal: Nuevo Condominio */}
      <Modal isOpen={showNewModal} onClose={() => setShowNewModal(false)} title="Nuevo Condominio" size="medium">
        <form onSubmit={handleCreateCondo} className="sa-form">
          <div className="sa-form-group">
            <label>Nombre del Condominio *</label>
            <input
              type="text"
              value={newCondo.name}
              onChange={e => setNewCondo({ ...newCondo, name: e.target.value })}
              placeholder="Ej: Residencial Las Palmas"
              required
            />
          </div>
          <div className="sa-form-group">
            <label>Dirección</label>
            <input
              type="text"
              value={newCondo.address}
              onChange={e => setNewCondo({ ...newCondo, address: e.target.value })}
              placeholder="Ej: Av. Principal 123"
            />
          </div>
          <div className="sa-form-row">
            <div className="sa-form-group">
              <label>Nombre del Administrador *</label>
              <input
                type="text"
                value={newCondo.admin_name}
                onChange={e => setNewCondo({ ...newCondo, admin_name: e.target.value })}
                placeholder="Nombre completo"
                required
              />
            </div>
            <div className="sa-form-group">
              <label>Email del Administrador *</label>
              <input
                type="email"
                value={newCondo.admin_email}
                onChange={e => setNewCondo({ ...newCondo, admin_email: e.target.value })}
                placeholder="admin@email.com"
                required
              />
            </div>
          </div>
          <div className="sa-form-group">
            <label>Plan de Suscripción</label>
            <select
              value={newCondo.subscription_plan}
              onChange={e => setNewCondo({ ...newCondo, subscription_plan: e.target.value })}
            >
              <option value="monthly">Mensual - $29.99/mes</option>
              <option value="quarterly">Trimestral - $79.99/trimestre</option>
              <option value="yearly">Anual - $299.99/año</option>
            </select>
          </div>
          <div className="sa-form-actions">
            <button type="button" className="sa-btn-outline" onClick={() => setShowNewModal(false)}>
              Cancelar
            </button>
            <button type="submit" className="sa-btn-primary" disabled={saving}>
              {saving ? <><FaSpinner className="spinning" /> Creando...</> : <><FaPlus /> Crear Condominio</>}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: Editar Condominio */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Editar Condominio" size="medium">
        {selectedCondo && (
          <form onSubmit={handleEditCondo} className="sa-form">
            <div className="sa-form-group">
              <label>Nombre del Condominio *</label>
              <input
                type="text"
                value={selectedCondo.name || ''}
                onChange={e => setSelectedCondo({ ...selectedCondo, name: e.target.value })}
                required
              />
            </div>
            <div className="sa-form-group">
              <label>Dirección</label>
              <input
                type="text"
                value={selectedCondo.address || ''}
                onChange={e => setSelectedCondo({ ...selectedCondo, address: e.target.value })}
              />
            </div>
            <div className="sa-form-group">
              <label>Estado de Suscripción</label>
              <select
                value={selectedCondo.subscription_status || 'active'}
                onChange={e => setSelectedCondo({ ...selectedCondo, subscription_status: e.target.value })}
              >
                <option value="active">Activo</option>
                <option value="pending">Pendiente</option>
                <option value="expired">Expirado</option>
                <option value="suspended">Suspendido</option>
              </select>
            </div>
            <div className="sa-form-actions">
              <button type="button" className="sa-btn-outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </button>
              <button type="submit" className="sa-btn-primary" disabled={saving}>
                {saving ? <><FaSpinner className="spinning" /> Guardando...</> : <><FaSave /> Guardar Cambios</>}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Modal: Ver Detalles */}
      <Modal isOpen={showDetailsModal} onClose={() => setShowDetailsModal(false)} title="Detalles del Condominio" size="large">
        {selectedCondo && (
          <div className="sa-details-view">
            <div className="sa-details-grid">
              <div className="sa-detail-item">
                <label>ID</label>
                <span>{selectedCondo.id}</span>
              </div>
              <div className="sa-detail-item">
                <label>Nombre</label>
                <span>{selectedCondo.name}</span>
              </div>
              <div className="sa-detail-item">
                <label>Dirección</label>
                <span>{selectedCondo.address || 'No especificada'}</span>
              </div>
              <div className="sa-detail-item">
                <label>Estado</label>
                <span className={`sa-status-badge ${selectedCondo.subscription_status?.toLowerCase()}`}>
                  {selectedCondo.subscription_status || 'Active'}
                </span>
              </div>
              <div className="sa-detail-item">
                <label>Total Residentes</label>
                <span>{selectedCondo.total_residents || 0}</span>
              </div>
              <div className="sa-detail-item">
                <label>Ingresos Totales</label>
                <span>${parseFloat(selectedCondo.total_revenue || 0).toLocaleString()}</span>
              </div>
              <div className="sa-detail-item">
                <label>Días Restantes</label>
                <span>{selectedCondo.days_remaining ?? 'N/A'}</span>
              </div>
              <div className="sa-detail-item">
                <label>Fecha Creación</label>
                <span>{selectedCondo.created_at ? new Date(selectedCondo.created_at).toLocaleDateString('es-EC') : 'N/A'}</span>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Estadísticas del Sistema (CON TABS FUNCIONALES)
// ═══════════════════════════════════════════════════════════════
const SystemStats = ({ systemStats, financialStats, loading, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('system');

  const cpuUsage = parseFloat(systemStats?.cpu?.usage) || 0;
  const cpuCores = systemStats?.cpu?.cores || 0;
  const memoryUsage = parseFloat(systemStats?.memory?.usage) || 0;
  const memoryUsed = formatBytes(systemStats?.memory?.used);
  const memoryTotal = formatBytes(systemStats?.memory?.total);
  const diskUsage = systemStats?.disk?.percentage || 0;
  const diskUsed = formatBytes(systemStats?.disk?.used);
  const diskTotal = formatBytes(systemStats?.disk?.total);
  const dbConnections = systemStats?.database?.connections || 0;
  const dbMaxConnections = systemStats?.database?.maxConnections || 151;
  const dbStatus = systemStats?.database?.status || 'unknown';
  const uptimeFormatted = systemStats?.uptime?.formatted || '0d 0h 0m';
  const nodeVersion = systemStats?.system?.nodeVersion || 'N/A';
  const platform = systemStats?.system?.platform || 'N/A';
  const buildDate = systemStats?.system?.buildDate || 'N/A';

  return (
    <div className="sa-section">
      <div className="sa-section-header">
        <div>
          <h2><FaChartBar /> Estadísticas</h2>
          <p>Monitoreo del sistema y finanzas</p>
        </div>
        <button className="sa-btn-refresh" onClick={onRefresh} disabled={loading}>
          <FaSync className={loading ? 'spinning' : ''} /> Actualizar
        </button>
      </div>

      <div className="sa-stats-tabs">
        <button
          className={`sa-tab ${activeTab === 'system' ? 'active' : ''}`}
          onClick={() => setActiveTab('system')}
        >
          <FaServer /> Sistema
        </button>
        <button
          className={`sa-tab ${activeTab === 'financial' ? 'active' : ''}`}
          onClick={() => setActiveTab('financial')}
        >
          <FaDollarSign /> Finanzas
        </button>
      </div>

      {activeTab === 'system' ? (
        <>
          <div className="sa-system-info-bar">
            <span>Version: <strong>{systemStats?.system?.version || '1.8.9.4.5.7'}</strong></span>
            <span>Build: <strong>{buildDate}</strong></span>
            <span>Node: <strong>{nodeVersion}</strong></span>
            <span>Plataforma: <strong>{platform}</strong></span>
            <span>Uptime: <strong>{uptimeFormatted}</strong></span>
          </div>

          <div className="sa-stats-grid">
            <SystemStatCard icon={FaMicrochip} title="CPU" value={`${cpuUsage}%`} subtitle={`${cpuCores} Cores`} color="#06b6d4" progress={cpuUsage} />
            <SystemStatCard icon={FaMemory} title="Memoria RAM" value={`${memoryUsage}%`} subtitle={`${memoryUsed} de ${memoryTotal}`} color="#f59e0b" progress={memoryUsage} />
            <SystemStatCard icon={FaHdd} title="Disco" value={`${diskUsage}%`} subtitle={`${diskUsed} de ${diskTotal}`} color="#10b981" progress={diskUsage} />
            <SystemStatCard icon={FaDatabase} title="Base de Datos" value={`${dbConnections}`} subtitle={`/ ${dbMaxConnections} Conexiones`} color="#d946ef" />
          </div>

          <div className="sa-db-status">
            <span className={`sa-status-indicator ${dbStatus === 'connected' ? 'connected' : 'disconnected'}`}>
              <FaDatabase />
              {dbStatus === 'connected' ? '✓ Conectado' : '✗ Desconectado'}
            </span>
            {systemStats?.timestamp && (
              <span className="sa-last-update">
                Última actualización: {new Date(systemStats.timestamp).toLocaleString('es-EC')}
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="sa-financial-stats">
          <div className="sa-metrics-grid">
            <div className="sa-metric large">
              <span className="sa-metric-label">Ingresos del Mes</span>
              <span className="sa-metric-value">${(financialStats?.monthlyRevenue || 0).toLocaleString()}</span>
            </div>
            <div className="sa-metric large">
              <span className="sa-metric-label">Ingresos del Año</span>
              <span className="sa-metric-value">${(financialStats?.yearlyRevenue || 0).toLocaleString()}</span>
            </div>
            <div className="sa-metric">
              <span className="sa-metric-label">Pagos Pendientes</span>
              <span className="sa-metric-value">{financialStats?.pendingPaymentsCount || 0}</span>
            </div>
            <div className="sa-metric">
              <span className="sa-metric-label">Monto Pendiente</span>
              <span className="sa-metric-value">${(financialStats?.pendingPaymentsAmount || 0).toLocaleString()}</span>
            </div>
          </div>

          {financialStats?.recentPayments?.length > 0 && (
            <div className="sa-recent-payments">
              <h3>Últimos Pagos</h3>
              <div className="sa-payments-table">
                <table>
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th>Condominio</th>
                      <th>Monto</th>
                      <th>Estado</th>
                      <th>Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialStats.recentPayments.slice(0, 10).map((payment, idx) => (
                      <tr key={idx}>
                        <td>{payment.user}</td>
                        <td>{payment.condominium}</td>
                        <td>${parseFloat(payment.amount).toFixed(2)}</td>
                        <td>
                          <span className={`sa-payment-status ${payment.status}`}>
                            {payment.status}
                          </span>
                        </td>
                        <td>{new Date(payment.date).toLocaleDateString('es-EC')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Reportería (CON GENERACIÓN REAL DE REPORTES)
// ═══════════════════════════════════════════════════════════════
const ReportsSection = ({ dashboardData, financialStats, showToast }) => {
  const [generating, setGenerating] = useState({});

  const generateReport = async (type, format) => {
    const key = `${type}-${format}`;
    setGenerating(prev => ({ ...prev, [key]: true }));

    try {
      const endpoint = API_ENDPOINTS.superAdminExportExcel ?
        API_ENDPOINTS.superAdminExportExcel(type) :
        `${API_URL}/api/superadmin/export/${type}`;

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Error al generar reporte');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reporte-${type}-${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      showToast(`Reporte de ${type} generado exitosamente`, 'success');
    } catch (error) {
      showToast(error.message || 'Error al generar reporte', 'error');
    } finally {
      setGenerating(prev => ({ ...prev, [key]: false }));
    }
  };

  const reports = [
    { type: 'users', icon: FaUsers, title: 'Reporte de Usuarios', desc: 'Lista completa de usuarios por condominio' },
    { type: 'financial', icon: FaDollarSign, title: 'Reporte Financiero', desc: 'Ingresos, pagos y morosidad' },
    { type: 'condominiums', icon: FaBuilding, title: 'Reporte de Condominios', desc: 'Estado y estadísticas por condominio' },
    { type: 'activity', icon: FaChartBar, title: 'Reporte de Actividad', desc: 'Logs y actividad del sistema' }
  ];

  return (
    <div className="sa-section">
      <div className="sa-section-header">
        <h2><FaClipboardList /> Reportería</h2>
        <p>Centro de reportes y análisis del sistema</p>
      </div>

      <div className="sa-reports-grid">
        {reports.map(report => (
          <div key={report.type} className="sa-report-card">
            <div className="sa-report-icon"><report.icon /></div>
            <h3>{report.title}</h3>
            <p>{report.desc}</p>
            <div className="sa-report-actions">
              <button
                className="sa-btn-outline"
                onClick={() => generateReport(report.type, 'xlsx')}
                disabled={generating[`${report.type}-xlsx`]}
              >
                {generating[`${report.type}-xlsx`] ? <FaSpinner className="spinning" /> : <FaFileExcel />}
                Excel
              </button>
              <button
                className="sa-btn-outline"
                onClick={() => generateReport(report.type, 'pdf')}
                disabled={generating[`${report.type}-pdf`]}
              >
                {generating[`${report.type}-pdf`] ? <FaSpinner className="spinning" /> : <FaFilePdf />}
                PDF
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="sa-quick-metrics">
        <h3>Métricas Financieras</h3>
        <div className="sa-metrics-grid">
          <div className="sa-metric">
            <span className="sa-metric-label">Ingresos del Mes</span>
            <span className="sa-metric-value">${(financialStats?.monthlyRevenue || 0).toLocaleString()}</span>
          </div>
          <div className="sa-metric">
            <span className="sa-metric-label">Ingresos del Año</span>
            <span className="sa-metric-value">${(financialStats?.yearlyRevenue || 0).toLocaleString()}</span>
          </div>
          <div className="sa-metric">
            <span className="sa-metric-label">Pagos Pendientes</span>
            <span className="sa-metric-value">{financialStats?.pendingPaymentsCount || 0}</span>
          </div>
          <div className="sa-metric">
            <span className="sa-metric-label">Monto Pendiente</span>
            <span className="sa-metric-value">${(financialStats?.pendingPaymentsAmount || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Configuración Global (CON TABS Y FUNCIONALIDAD REAL)
// ═══════════════════════════════════════════════════════════════
const GlobalSettings = ({ settings, user, onSaveSettings, showToast }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [localSettings, setLocalSettings] = useState({
    general: {
      systemName: settings?.general?.systemName || 'UrbanQ',
      version: settings?.general?.version || '1.8.9.4.5.7',
      language: settings?.general?.language || 'es',
      timezone: settings?.general?.timezone || 'America/Guayaquil',
      maintenanceMode: settings?.general?.maintenanceMode || false,
      debugMode: settings?.general?.debugMode || false
    },
    email: {
      smtpHost: settings?.email?.smtpHost || '',
      smtpPort: settings?.email?.smtpPort || 587,
      smtpUser: settings?.email?.smtpUser || '',
      smtpPassword: settings?.email?.smtpPassword || '',
      fromEmail: settings?.email?.fromEmail || '',
      fromName: settings?.email?.fromName || 'UrbanQ'
    },
    paypal: {
      clientId: settings?.paypal?.clientId || '',
      clientSecret: settings?.paypal?.clientSecret || '',
      mode: settings?.paypal?.mode || 'sandbox'
    },
    backup: {
      autoBackup: settings?.backup?.autoBackup || false,
      frequency: settings?.backup?.frequency || 'daily',
      retention: settings?.backup?.retention || 30,
      time: settings?.backup?.time || '02:00'
    },
    security: {
      twoFactorEnabled: settings?.security?.twoFactorEnabled || false,
      sessionTimeout: settings?.security?.sessionTimeout || 60,
      maxLoginAttempts: settings?.security?.maxLoginAttempts || 5
    }
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (settings) {
      setLocalSettings(prev => ({
        general: { ...prev.general, ...settings.general },
        email: { ...prev.email, ...settings.email },
        paypal: { ...prev.paypal, ...settings.paypal },
        backup: { ...prev.backup, ...settings.backup },
        security: { ...prev.security, ...settings.security }
      }));
    }
  }, [settings]);

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const response = await apiRequest(API_ENDPOINTS.superAdminSettings, {
        method: 'PUT',
        body: JSON.stringify({ [section]: localSettings[section] })
      });
      if (response.success) {
        showToast(`Configuración de ${section} guardada exitosamente`, 'success');
        if (onSaveSettings) onSaveSettings();
      }
    } catch (error) {
      showToast(error.message || 'Error al guardar configuración', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleChange = async (section, field, value) => {
    const newSettings = {
      ...localSettings,
      [section]: { ...localSettings[section], [field]: value }
    };
    setLocalSettings(newSettings);

    try {
      await apiRequest(API_ENDPOINTS.superAdminSettings, {
        method: 'PUT',
        body: JSON.stringify({ [section]: { [field]: value } })
      });
      showToast(`${field === 'maintenanceMode' ? 'Modo mantenimiento' : 'Configuración'} actualizado`, 'success');
    } catch (error) {
      setLocalSettings(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: !value }
      }));
      showToast('Error al actualizar configuración', 'error');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast('Las contraseñas no coinciden', 'error');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      showToast('La contraseña debe tener al menos 8 caracteres', 'error');
      return;
    }

    setSaving(true);
    try {
      const response = await apiRequest(API_ENDPOINTS.changePassword, {
        method: 'PUT',
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });
      if (response.success) {
        showToast('Contraseña actualizada exitosamente', 'success');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error) {
      showToast(error.message || 'Error al cambiar contraseña', 'error');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', icon: FaCog, label: 'General' },
    { id: 'email', icon: FaEnvelope, label: 'Email' },
    { id: 'paypal', icon: FaPaypal, label: 'PayPal' },
    { id: 'backup', icon: FaSave, label: 'Respaldos' },
    { id: 'security', icon: FaShieldAlt, label: 'Seguridad' }
  ];

  return (
    <div className="sa-section">
      <div className="sa-section-header">
        <h2><FaCog /> Configuración del Sistema</h2>
        <p>Administra las configuraciones globales de UrbanQ</p>
      </div>

      <div className="sa-settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`sa-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon /> {tab.label}
          </button>
        ))}
      </div>

      <div className="sa-settings-panel">
        {/* TAB: General */}
        {activeTab === 'general' && (
          <>
            <h3>Configuración General</h3>
            <p className="sa-settings-subtitle">Ajustes básicos del sistema</p>

            <div className="sa-settings-form">
              <div className="sa-form-group">
                <label>NOMBRE DEL SISTEMA</label>
                <input
                  type="text"
                  value={localSettings.general.systemName}
                  onChange={e => setLocalSettings(prev => ({
                    ...prev,
                    general: { ...prev.general, systemName: e.target.value }
                  }))}
                />
              </div>

              <div className="sa-form-group">
                <label>VERSION</label>
                <input type="text" value={localSettings.general.version} readOnly />
              </div>

              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>IDIOMA POR DEFECTO</label>
                  <select
                    value={localSettings.general.language}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      general: { ...prev.general, language: e.target.value }
                    }))}
                  >
                    <option value="es">Español</option>
                    <option value="en">English</option>
                  </select>
                </div>

                <div className="sa-form-group">
                  <label>ZONA HORARIA</label>
                  <select
                    value={localSettings.general.timezone}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      general: { ...prev.general, timezone: e.target.value }
                    }))}
                  >
                    <option value="America/Guayaquil">America/Guayaquil (ECT)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                    <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                    <option value="Europe/Madrid">Europe/Madrid (CET)</option>
                  </select>
                </div>
              </div>

              <div className="sa-form-toggles">
                <div className="sa-toggle-item">
                  <div>
                    <h4>Modo Mantenimiento</h4>
                    <p>Solo SuperAdmin puede acceder cuando está activo</p>
                  </div>
                  <label className="sa-toggle">
                    <input
                      type="checkbox"
                      checked={localSettings.general.maintenanceMode}
                      onChange={e => handleToggleChange('general', 'maintenanceMode', e.target.checked)}
                    />
                    <span className="sa-toggle-slider"></span>
                  </label>
                </div>

                <div className="sa-toggle-item">
                  <div>
                    <h4>Modo Debug</h4>
                    <p>Muestra errores detallados (solo desarrollo)</p>
                  </div>
                  <label className="sa-toggle">
                    <input
                      type="checkbox"
                      checked={localSettings.general.debugMode}
                      onChange={e => handleToggleChange('general', 'debugMode', e.target.checked)}
                    />
                    <span className="sa-toggle-slider"></span>
                  </label>
                </div>
              </div>

              <button className="sa-btn-primary sa-btn-save" onClick={() => handleSave('general')} disabled={saving}>
                {saving ? <><FaSpinner className="spinning" /> Guardando...</> : <><FaSave /> Guardar Configuración General</>}
              </button>
            </div>
          </>
        )}

        {/* TAB: Email */}
        {activeTab === 'email' && (
          <>
            <h3>Configuración de Email (SMTP)</h3>
            <p className="sa-settings-subtitle">Configura el servidor de correo saliente</p>

            <div className="sa-settings-form">
              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>SERVIDOR SMTP</label>
                  <input
                    type="text"
                    value={localSettings.email.smtpHost}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, smtpHost: e.target.value }
                    }))}
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div className="sa-form-group">
                  <label>PUERTO</label>
                  <input
                    type="number"
                    value={localSettings.email.smtpPort}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, smtpPort: parseInt(e.target.value) }
                    }))}
                    placeholder="587"
                  />
                </div>
              </div>

              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>USUARIO SMTP</label>
                  <input
                    type="text"
                    value={localSettings.email.smtpUser}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, smtpUser: e.target.value }
                    }))}
                    placeholder="usuario@gmail.com"
                  />
                </div>
                <div className="sa-form-group">
                  <label>CONTRASEÑA SMTP</label>
                  <input
                    type="password"
                    value={localSettings.email.smtpPassword}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, smtpPassword: e.target.value }
                    }))}
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>EMAIL REMITENTE</label>
                  <input
                    type="email"
                    value={localSettings.email.fromEmail}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, fromEmail: e.target.value }
                    }))}
                    placeholder="noreply@urbanq.com"
                  />
                </div>
                <div className="sa-form-group">
                  <label>NOMBRE REMITENTE</label>
                  <input
                    type="text"
                    value={localSettings.email.fromName}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      email: { ...prev.email, fromName: e.target.value }
                    }))}
                    placeholder="UrbanQ"
                  />
                </div>
              </div>

              <button className="sa-btn-primary sa-btn-save" onClick={() => handleSave('email')} disabled={saving}>
                {saving ? <><FaSpinner className="spinning" /> Guardando...</> : <><FaSave /> Guardar Configuración Email</>}
              </button>
            </div>
          </>
        )}

        {/* TAB: PayPal */}
        {activeTab === 'paypal' && (
          <>
            <h3>Configuración de PayPal</h3>
            <p className="sa-settings-subtitle">Configura las credenciales de PayPal para pagos</p>

            <div className="sa-settings-form">
              <div className="sa-form-group">
                <label>CLIENT ID</label>
                <input
                  type="text"
                  value={localSettings.paypal.clientId}
                  onChange={e => setLocalSettings(prev => ({
                    ...prev,
                    paypal: { ...prev.paypal, clientId: e.target.value }
                  }))}
                  placeholder="AYSq3RDGsmBLJE..."
                />
              </div>

              <div className="sa-form-group">
                <label>CLIENT SECRET</label>
                <input
                  type="password"
                  value={localSettings.paypal.clientSecret}
                  onChange={e => setLocalSettings(prev => ({
                    ...prev,
                    paypal: { ...prev.paypal, clientSecret: e.target.value }
                  }))}
                  placeholder="••••••••••••••••"
                />
              </div>

              <div className="sa-form-group">
                <label>MODO</label>
                <select
                  value={localSettings.paypal.mode}
                  onChange={e => setLocalSettings(prev => ({
                    ...prev,
                    paypal: { ...prev.paypal, mode: e.target.value }
                  }))}
                >
                  <option value="sandbox">Sandbox (Pruebas)</option>
                  <option value="live">Live (Producción)</option>
                </select>
              </div>

              <div className="sa-info-box">
                <FaExclamationTriangle />
                <p>Asegúrate de usar las credenciales correctas según el modo seleccionado. Las credenciales de Sandbox no funcionarán en modo Live y viceversa.</p>
              </div>

              <button className="sa-btn-primary sa-btn-save" onClick={() => handleSave('paypal')} disabled={saving}>
                {saving ? <><FaSpinner className="spinning" /> Guardando...</> : <><FaSave /> Guardar Configuración PayPal</>}
              </button>
            </div>
          </>
        )}

        {/* TAB: Respaldos */}
        {activeTab === 'backup' && (
          <>
            <h3>Configuración de Respaldos Automáticos</h3>
            <p className="sa-settings-subtitle">Programa respaldos automáticos del sistema</p>

            <div className="sa-settings-form">
              <div className="sa-form-toggles">
                <div className="sa-toggle-item">
                  <div>
                    <h4>Respaldos Automáticos</h4>
                    <p>Activar respaldos programados automáticamente</p>
                  </div>
                  <label className="sa-toggle">
                    <input
                      type="checkbox"
                      checked={localSettings.backup.autoBackup}
                      onChange={e => setLocalSettings(prev => ({
                        ...prev,
                        backup: { ...prev.backup, autoBackup: e.target.checked }
                      }))}
                    />
                    <span className="sa-toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>FRECUENCIA</label>
                  <select
                    value={localSettings.backup.frequency}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      backup: { ...prev.backup, frequency: e.target.value }
                    }))}
                    disabled={!localSettings.backup.autoBackup}
                  >
                    <option value="hourly">Cada Hora</option>
                    <option value="daily">Diario</option>
                    <option value="weekly">Semanal</option>
                    <option value="monthly">Mensual</option>
                  </select>
                </div>
                <div className="sa-form-group">
                  <label>HORA DE EJECUCIÓN</label>
                  <input
                    type="time"
                    value={localSettings.backup.time}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      backup: { ...prev.backup, time: e.target.value }
                    }))}
                    disabled={!localSettings.backup.autoBackup}
                  />
                </div>
              </div>

              <div className="sa-form-group">
                <label>RETENCIÓN (DÍAS)</label>
                <input
                  type="number"
                  value={localSettings.backup.retention}
                  onChange={e => setLocalSettings(prev => ({
                    ...prev,
                    backup: { ...prev.backup, retention: parseInt(e.target.value) }
                  }))}
                  min="1"
                  max="365"
                  disabled={!localSettings.backup.autoBackup}
                />
                <span className="sa-form-hint">Los respaldos más antiguos se eliminarán automáticamente</span>
              </div>

              <button className="sa-btn-primary sa-btn-save" onClick={() => handleSave('backup')} disabled={saving}>
                {saving ? <><FaSpinner className="spinning" /> Guardando...</> : <><FaSave /> Guardar Configuración de Respaldos</>}
              </button>
            </div>
          </>
        )}

        {/* TAB: Seguridad */}
        {activeTab === 'security' && (
          <>
            <h3>Configuración de Seguridad</h3>
            <p className="sa-settings-subtitle">Ajustes de seguridad y autenticación</p>

            <div className="sa-settings-form">
              <div className="sa-form-toggles">
                <div className="sa-toggle-item">
                  <div>
                    <h4>Autenticación de Dos Factores (2FA)</h4>
                    <p>Requerir código de verificación al iniciar sesión</p>
                  </div>
                  <label className="sa-toggle">
                    <input
                      type="checkbox"
                      checked={localSettings.security.twoFactorEnabled}
                      onChange={e => handleToggleChange('security', 'twoFactorEnabled', e.target.checked)}
                    />
                    <span className="sa-toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="sa-form-row">
                <div className="sa-form-group">
                  <label>TIEMPO DE SESIÓN (MINUTOS)</label>
                  <input
                    type="number"
                    value={localSettings.security.sessionTimeout}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      security: { ...prev.security, sessionTimeout: parseInt(e.target.value) }
                    }))}
                    min="5"
                    max="1440"
                  />
                </div>
                <div className="sa-form-group">
                  <label>INTENTOS DE LOGIN MÁXIMOS</label>
                  <input
                    type="number"
                    value={localSettings.security.maxLoginAttempts}
                    onChange={e => setLocalSettings(prev => ({
                      ...prev,
                      security: { ...prev.security, maxLoginAttempts: parseInt(e.target.value) }
                    }))}
                    min="3"
                    max="10"
                  />
                </div>
              </div>

              <button className="sa-btn-primary sa-btn-save" onClick={() => handleSave('security')} disabled={saving}>
                {saving ? <><FaSpinner className="spinning" /> Guardando...</> : <><FaSave /> Guardar Configuración de Seguridad</>}
              </button>

              <hr className="sa-divider" />

              <h3><FaKey /> Cambiar Contraseña</h3>
              <form onSubmit={handleChangePassword} className="sa-password-form">
                <div className="sa-form-group">
                  <label>CONTRASEÑA ACTUAL</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={e => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    required
                  />
                </div>
                <div className="sa-form-row">
                  <div className="sa-form-group">
                    <label>NUEVA CONTRASEÑA</label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={e => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      required
                      minLength="8"
                    />
                  </div>
                  <div className="sa-form-group">
                    <label>CONFIRMAR CONTRASEÑA</label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={e => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <button type="submit" className="sa-btn-primary" disabled={saving}>
                  {saving ? <><FaSpinner className="spinning" /> Cambiando...</> : <><FaLock /> Cambiar Contraseña</>}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Respaldos (CON FUNCIONALIDAD COMPLETA)
// ═══════════════════════════════════════════════════════════════
const BackupsSection = ({ backups, onCreateBackup, onRefresh, showToast }) => {
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState({});

  const handleCreate = async () => {
    setCreating(true);
    await onCreateBackup();
    setCreating(false);
  };

  const handleDownload = async (backup) => {
    const key = backup.filename || backup.id;
    setDownloading(prev => ({ ...prev, [key]: true }));

    try {
      const endpoint = API_ENDPOINTS.superAdminBackupDownload ?
        API_ENDPOINTS.superAdminBackupDownload(backup.id || backup.filename) :
        `${API_URL}/api/superadmin/backups/${backup.id || backup.filename}/download`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) throw new Error('Error al descargar backup');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = backup.filename || `backup-${new Date().toISOString()}.sql`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      showToast('Backup descargado exitosamente', 'success');
    } catch (error) {
      showToast(error.message || 'Error al descargar backup', 'error');
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDelete = async (backup) => {
    if (!window.confirm(`¿Estás seguro de eliminar el backup "${backup.filename}"?`)) {
      return;
    }

    try {
      const endpoint = API_ENDPOINTS.superAdminBackupDelete ?
        API_ENDPOINTS.superAdminBackupDelete(backup.filename) :
        `${API_URL}/api/superadmin/backups/${backup.filename}`;

      const response = await apiRequest(endpoint, { method: 'DELETE' });
      if (response.success) {
        showToast('Backup eliminado', 'success');
        onRefresh();
      }
    } catch (error) {
      showToast(error.message || 'Error al eliminar backup', 'error');
    }
  };

  return (
    <div className="sa-section">
      <div className="sa-section-header">
        <div>
          <h2><FaSave /> Respaldos</h2>
          <p>Gestión de copias de seguridad del sistema</p>
        </div>
        <button className="sa-btn-refresh" onClick={onRefresh}>
          <FaSync />
        </button>
      </div>

      <div className="sa-backup-actions">
        <button className="sa-btn-primary" onClick={handleCreate} disabled={creating}>
          {creating ? <><FaSpinner className="spinning" /> Creando...</> : <><FaDatabase /> Crear Respaldo Manual</>}
        </button>
      </div>

      <div className="sa-backup-list">
        {backups && backups.length > 0 ? (
          backups.map((backup, idx) => {
            const key = backup.filename || backup.id || idx;
            return (
              <div key={key} className="sa-backup-item">
                <div className="sa-backup-info">
                  <span className="sa-backup-name">{backup.filename}</span>
                  <span className="sa-backup-size">{backup.size ? formatBytes(backup.size) : 'N/A'}</span>
                </div>
                <span className="sa-backup-date">
                  {backup.created_at ? new Date(backup.created_at).toLocaleString('es-EC') : 'N/A'}
                </span>
                <div className="sa-backup-buttons">
                  <button
                    className="sa-btn-outline"
                    onClick={() => handleDownload(backup)}
                    disabled={downloading[key]}
                  >
                    {downloading[key] ? <FaSpinner className="spinning" /> : <FaDownload />}
                    Descargar
                  </button>
                  <button className="sa-btn-outline danger" onClick={() => handleDelete(backup)}>
                    <FaTrash />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="sa-empty-state">
            <FaSave />
            <p>No hay respaldos recientes</p>
            <span>Crea tu primer respaldo manual o configura respaldos automáticos</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL: SuperAdminDashboard
// ═══════════════════════════════════════════════════════════════
function SuperAdminDashboard({ user, onLogout }) {
  // Estados
  const [activeSection, setActiveSection] = useState('inicio');
  const [dashboardData, setDashboardData] = useState({});
  const [systemStats, setSystemStats] = useState({});
  const [financialStats, setFinancialStats] = useState({});
  const [projects, setProjects] = useState([]);
  const [settings, setSettings] = useState({});
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [toast, setToast] = useState(null);

  // Toast helper
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  // Fetch functions
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.superAdminDashboard);
      if (response.success) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, []);

  const fetchSystemStats = useCallback(async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.superAdminSystemStats);
      if (response.success) {
        setSystemStats(response.data);
        setIsOnline(true);
      }
    } catch (error) {
      console.error('Error fetching system stats:', error);
      setIsOnline(false);
    }
  }, []);

  const fetchFinancialStats = useCallback(async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.superAdminFinancialStats);
      if (response.success) {
        setFinancialStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching financial stats:', error);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.superAdminProjects);
      if (response.success) {
        setProjects(response.data.projects || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.superAdminSettings);
      if (response.success) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  const fetchBackups = useCallback(async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.superAdminBackups);
      if (response.success) {
        setBackups(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
    }
  }, []);

  const createBackup = async () => {
    try {
      const response = await apiRequest(API_ENDPOINTS.superAdminBackups, {
        method: 'POST'
      });
      if (response.success) {
        showToast('Backup creado exitosamente', 'success');
        fetchBackups();
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      showToast('Error al crear backup', 'error');
    }
  };

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      await Promise.all([
        fetchDashboard(),
        fetchSystemStats(),
        fetchFinancialStats(),
        fetchProjects(),
        fetchSettings(),
        fetchBackups()
      ]);

      setIsOnline(true);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Error al cargar los datos del sistema');
      setIsOnline(false);
    } finally {
      setLoading(false);
    }
  }, [fetchDashboard, fetchSystemStats, fetchFinancialStats, fetchProjects, fetchSettings, fetchBackups]);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(() => {
      fetchSystemStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchAllData, fetchSystemStats]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [activeSection]);

  const renderSection = () => {
    if (loading && !dashboardData.totalCondominiums) {
      return (
        <div className="sa-loading">
          <div className="sa-loading-spinner"></div>
          <p>Cargando datos del sistema...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="sa-error">
          <FaExclamationTriangle className="sa-error-icon" />
          <h3>Error del Sistema</h3>
          <p>{error}</p>
          <button className="sa-btn-primary" onClick={fetchAllData}>
            <FaSync /> Reintentar
          </button>
        </div>
      );
    }

    switch(activeSection) {
      case 'condominiums':
        return <CondominiumsManager projects={projects} loading={loading} onRefresh={fetchProjects} showToast={showToast} />;
      case 'stats':
        return <SystemStats systemStats={systemStats} financialStats={financialStats} loading={loading} onRefresh={fetchSystemStats} />;
      case 'reports':
        return <ReportsSection dashboardData={dashboardData} financialStats={financialStats} showToast={showToast} />;
      case 'settings':
        return <GlobalSettings settings={settings} user={user} onSaveSettings={fetchSettings} showToast={showToast} />;
      case 'backups':
        return <BackupsSection backups={backups} onCreateBackup={createBackup} onRefresh={fetchBackups} showToast={showToast} />;
      case 'inicio':
      default:
        return (
          <SuperAdminInicio
            user={user}
            dashboardData={dashboardData}
            systemStats={systemStats}
            setActiveSection={setActiveSection}
          />
        );
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    onLogout();
  };

  const menuItems = [
    { id: 'inicio', icon: FaHome, label: 'Inicio' },
    { id: 'urbanq', label: 'UrbanQ', isHeader: true },
    { id: 'condominiums', icon: FaBuilding, label: 'Condominios', indent: true },
    { id: 'admins', icon: FaUsers, label: 'Administradores', indent: true },
    { id: 'stats', icon: FaChartBar, label: 'Estadísticas' },
    { id: 'settings', icon: FaCog, label: 'Configuración' },
    { id: 'reports', icon: FaClipboardList, label: 'Reportería' },
    { id: 'backups', icon: FaSave, label: 'Respaldos' }
  ];

  return (
    <div className={`sa-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Toast Notification */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}

      {/* Overlay para móvil */}
      {mobileMenuOpen && (
        <div className="sa-overlay" onClick={() => setMobileMenuOpen(false)}></div>
      )}

      {/* SIDEBAR */}
      <aside className={`sa-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sa-sidebar-header">
          <div className="sa-logo">
            <FaShieldAlt className="sa-logo-icon" />
            {!sidebarCollapsed && (
              <div className="sa-logo-text">
                <span className="sa-logo-title">Global Panel</span>
                <span className="sa-logo-subtitle">SUPERADMIN</span>
              </div>
            )}
          </div>
          <button
            className="sa-collapse-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expandir' : 'Colapsar'}
          >
            {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        <div className="sa-user-section">
          <div className="sa-user-avatar">
            <FaUser />
          </div>
          {!sidebarCollapsed && (
            <div className="sa-user-info">
              <span className="sa-user-name">{user.name}</span>
              <span className="sa-user-role">{user.role}</span>
              <div className="sa-user-status">
                <div className="sa-status-dot"></div>
                <span>En línea</span>
              </div>
            </div>
          )}
        </div>

        {!sidebarCollapsed && (
          <div className="sa-mini-stats">
            <div className="sa-mini-stat">
              <span className="sa-mini-label">CPU</span>
              <span className="sa-mini-value">{parseFloat(systemStats?.cpu?.usage || 0).toFixed(1)}%</span>
            </div>
            <div className="sa-mini-stat">
              <span className="sa-mini-label">RAM</span>
              <span className="sa-mini-value">{parseFloat(systemStats?.memory?.usage || 0).toFixed(1)}%</span>
            </div>
          </div>
        )}

        <nav className="sa-nav">
          {menuItems.map(item => {
            if (item.isHeader) {
              return !sidebarCollapsed ? (
                <div key={item.id} className="sa-nav-header">
                  <FaChevronRight className="sa-nav-arrow" />
                  {item.label}
                </div>
              ) : null;
            }

            return (
              <button
                key={item.id}
                className={`sa-nav-item ${activeSection === item.id ? 'active' : ''} ${item.indent ? 'indent' : ''}`}
                onClick={() => setActiveSection(item.id)}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <item.icon className="sa-nav-icon" />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {!sidebarCollapsed && (
          <div className="sa-version">
            <span>v{systemStats?.system?.version || '1.8.9.4.5.7'}</span>
            <span>Build {systemStats?.system?.buildDate || '2026-02-19'}</span>
          </div>
        )}

        <button className="sa-logout-btn" onClick={handleLogout} title="Cerrar Sesión">
          <FaSignOutAlt />
          {!sidebarCollapsed && <span>Cerrar Sesión</span>}
        </button>
      </aside>

      {/* CONTENIDO PRINCIPAL */}
      <main className="sa-main">
        <header className="sa-mobile-header">
          <button className="sa-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
          <div className="sa-mobile-title">
            <FaShieldAlt />
            <span>Global Panel</span>
          </div>
          <OnlineIndicator isOnline={isOnline} />
        </header>

        <header className="sa-header">
          <div className="sa-header-left">
            <h1>{getSectionTitle(activeSection)}</h1>
            <p>{getSectionSubtitle(activeSection)}</p>
          </div>
          <OnlineIndicator isOnline={isOnline} />
        </header>

        <div className="sa-content">
          {renderSection()}
        </div>

        <footer className="sa-footer">
          <div className="sa-footer-left">
            <FaShieldAlt />
            <span>UrbanQ SuperAdmin</span>
          </div>
          <div className="sa-footer-center">
            <span><FaServer /> Uptime: {systemStats?.uptime?.formatted || '0d 0h 0m'}</span>
            <span><FaDatabase /> DB: {systemStats?.database?.status === 'connected' ? 'Conectada' : 'Verificando...'}</span>
            <span><FaGlobe /> Servidor: {isOnline ? 'Activo' : 'Inactivo'}</span>
          </div>
          <div className="sa-footer-right">
            <span>© 2026 NextUnicode Corp</span>
          </div>
        </footer>
      </main>

      {/* Widget UrbanIA - Asistente IA Local */}
      <SuperAdminAIChat setActiveSection={setActiveSection} />
    </div>
  );
}

const getSectionTitle = (section) => {
  const titles = {
    inicio: 'Panel de Inicio',
    condominiums: 'Condominios',
    admins: 'Administradores',
    stats: 'Estadísticas',
    settings: 'Configuración del Sistema',
    reports: 'Reportería',
    backups: 'Respaldos'
  };
  return titles[section] || 'Global Panel';
};

const getSectionSubtitle = (section) => {
  const subtitles = {
    inicio: 'Resumen general del sistema',
    condominiums: 'Gestiona todos los condominios',
    admins: 'Gestiona los administradores',
    stats: 'Monitoreo del sistema y finanzas',
    settings: 'Administra las configuraciones globales de UrbanQ',
    reports: 'Centro de reportes y análisis',
    backups: 'Gestión de copias de seguridad'
  };
  return subtitles[section] || '';
};

export default SuperAdminDashboard;
